import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import useInfiniteScroll, { FEED_PAGE_SIZE } from "../../hooks/useInfiniteScroll";
import { usePagedFeed, useEnsureFeedCount, feedMayHaveMore } from "../../hooks/usePagedFeed";
import { T } from "../Translate";
import {
  Container,
  AppBar,
  Toolbar,
  CardMedia,
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Skeleton,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { Flight as FlightIcon } from "../../icons";
import logoSvg from "../../imgs/logo.png";
import logo1 from "../../imgs/logo1.png";
import BottomToolbar from "../BottomToolbar";
import CustomToolbar from "../CustomToolbar";
import { brandColors, barColors, themeMode } from "../../theme";
import ListingImageSlideshow from "../../components/ListingImageSlideshow";
import ListingPost, { useListingSocial } from "../../components/ListingPost";
import useAuthorPhotos from "../useAuthorPhotos";
import { featuredFirst, isFeatured } from "../../utils/featuredListings";
import { pageContentSx, DESKTOP_MEDIA_QUERY, PAGE_CONTENT_MAX_WIDTH, FILTER_PANEL_BOTTOM_GAP, filterPanelScrollSx, listingCardSx, listingBodySx, listingMediaSx, listingContentSx, feedPostHeaderSx, listingImageHeight, listingImageRadius } from "../../pageLayout";
import { useDesktopSidebar } from "../../desktopSidebarContext";
import { useGeolocation } from "../../hooks/useGeolocation";
import { getCityFromCoordinates } from "../../utils/geo";
import SheetDragHandle from "../../components/SheetDragHandle";

import SearchClearAdornment from "../../components/SearchClearAdornment";
const mapPlaceDoc = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    imageUrls: Array.isArray(data.images) ? data.images : [data.images].filter(Boolean),
  };
};

const logo5 = `${process.env.PUBLIC_URL}/logo5.png`;

const PROPERTY_TYPES = [
  { id: "alojamiento", label: <T>Alojamiento</T> },
  { id: "compra", label: <T>Compra</T> },
  { id: "alquiler", label: <T>Alquiler</T> },
  { id: "combo", label: <T>Combo Vuelo + Alojamiento</T> },
];

const REGIONS = [
  <T key="na">América del Norte</T>,
  <T key="sa">América del Sur</T>,
  <T key="eu">Europa</T>,
  <T key="as">Asia</T>,
  <T key="me">Medio Oriente</T>,
  <T key="af">África</T>,
  <T key="oc">Oceanía</T>,
  <T key="an">Antártida</T>,
];

// La región (antes se elegía tocando un punto en un globo 3D) ahora es un
// filtro más — hace falta el nombre en texto plano (no el elemento <T>) para
// poder comparar contra place.place con .toLowerCase().
const REGION_OPTIONS = [
  "América del Norte",
  "América del Sur",
  "Europa",
  "Asia",
  "Medio Oriente",
  "África",
  "Oceanía",
  "Antártida",
];

const TripsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const sidebarActive = useDesktopSidebar();
  // Con la sidebar el logo y las categorías viven en ella: el AppBar solo
  // queda (plano) para los íconos de buscar/filtros cuando no hay panel lateral.
  const showTopBar = !sidebarActive || !isDesktop;
  const navigate = useNavigate();

  // Propiedades de a tandas desde Firestore (ver usePagedFeed): antes se
  // escuchaba la colección entera. Los filtros se aplican sobre lo cargado.
  const placesFeed = usePagedFeed("places", mapPlaceDoc);
  const social = useListingSocial("place");
  const authorPhotos = useAuthorPhotos(placesFeed.items.map((place) => place.uid));
  const places = placesFeed.items;
  const [filteredPlaces, setFilteredPlaces] = useState([]);

  const [selectedType, setSelectedType] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCity, setFilterCity] = useState("");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");

  // Mide el alto real del AppBar fijo — mismo criterio que
  // home.js/events.js/cars.js.
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(96);
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) {
      setAppBarHeight(0);
      return;
    }
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isSearchOpen, showTopBar]);

  const toggleSearch = () => setIsSearchOpen((open) => !open);

  // Ciudad del usuario (geolocalización) para priorizar en el orden por
  // defecto las propiedades publicadas en su misma ciudad — ver mismo
  // criterio en cars.js.
  const { position } = useGeolocation();
  const [userCity, setUserCity] = useState(null);
  useEffect(() => {
    if (!position) return;
    getCityFromCoordinates(position.latitude, position.longitude).then(setUserCity);
  }, [position]);

  useEffect(() => {
    if (!places.length) return;

    const term = searchTerm.trim().toLowerCase();
    const city = filterCity.trim().toLowerCase();
    const priceMin = parseFloat(filterPriceMin);
    const priceMax = parseFloat(filterPriceMax);

    const filtered = places.filter((place) => {
      const placePrice = parseFloat(place.price);
      return (
        (!selectedType ||
          (typeof place.category === 'string' && place.category.toLowerCase() === selectedType.toLowerCase())) &&
        (!selectedRegion ||
          (typeof place.place === 'string' && place.place.toLowerCase().includes(selectedRegion.toLowerCase()))) &&
        (!term || [place.title, place.city, place.description].some(
          (field) => typeof field === 'string' && field.toLowerCase().includes(term)
        )) &&
        (!city || (typeof place.city === 'string' && place.city.toLowerCase().includes(city))) &&
        (isNaN(priceMin) || (!isNaN(placePrice) && placePrice >= priceMin)) &&
        (isNaN(priceMax) || (!isNaN(placePrice) && placePrice <= priceMax))
      );
    });

    // Orden por defecto: propiedades de la ciudad del usuario primero (sort
    // es estable, así que dentro de cada grupo se conserva el orden por
    // fecha que ya trae la query de Firestore).
    if (userCity) {
      const matchesUserCity = (place) =>
        typeof place.city === 'string' && place.city.toLowerCase() === userCity.toLowerCase();
      filtered.sort((a, b) => Number(matchesUserCity(b)) - Number(matchesUserCity(a)));
    }

    // Las destacadas (pagaron "Destacar publicación") van siempre primero.
    setFilteredPlaces(featuredFirst(filtered));
  }, [places, selectedType, selectedRegion, searchTerm, filterCity, filterPriceMin, filterPriceMax, userCity]);

  // Muestra los alojamientos de a 13 y suma otros 13 al acercarse al último.
  const { limit: placesLimit, sentinelRef: placesSentinelRef } = useInfiniteScroll(
    JSON.stringify([selectedType, selectedRegion, searchTerm, filterCity, filterPriceMin, filterPriceMax]),
    FEED_PAGE_SIZE
  );
  // Si con las propiedades cargadas no alcanzan los resultados, se piden más.
  useEnsureFeedCount(placesFeed, filteredPlaces.length, placesLimit);
  const loading = filteredPlaces.length === 0 && feedMayHaveMore(placesFeed);

  const hasActiveFilters = Boolean(
    searchTerm || selectedType || selectedRegion || filterCity || filterPriceMin || filterPriceMax
  );

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedType("");
    setSelectedRegion("");
    setFilterCity("");
    setFilterPriceMin("");
    setFilterPriceMax("");
  };

  const styles = {
    header: {
      position: "fixed",
      top: 0,
      width: "100%",
      bgcolor: barColors.background,
      zIndex: 1100,
    },
    logoToolbar: {
      display: "flex",
      justifyContent: "center",
      padding: "8px 0",
    },
    // Mismo tamaño de logo y de Toolbar que home.js/events.js, para que el
    // AppBar tenga el mismo alto en todas las secciones.
    logo: {
      width: "130px",
      height: "auto",
      aspectRatio: "847 / 231",
    },
  };

  const SkeletonPlaceCard = () => (
    <Box sx={{ ...listingCardSx, backgroundColor: brandColors.backgroundPaper }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, ...feedPostHeaderSx }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
      <Box sx={listingBodySx}>
        <Box sx={listingMediaSx}>
          <Skeleton variant="rectangular" width="100%" sx={{ height: listingImageHeight, borderRadius: listingImageRadius }} />
        </Box>
        <Box sx={listingContentSx}>
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            <Skeleton variant="rounded" width={70} height={24} />
            <Skeleton variant="rounded" width={70} height={24} />
          </Box>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
      </Box>
    </Box>
  );

  const filtersFields = (
    <>
      <TextField
        select
        fullWidth
        size="small"
        label={<T>Tipo</T>}
        value={selectedType}
        onChange={(e) => setSelectedType(e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value=""><T>Todos</T></MenuItem>
        {PROPERTY_TYPES.map((type) => (
          <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        label={<T>Región</T>}
        value={selectedRegion}
        onChange={(e) => setSelectedRegion(e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value=""><T>Todas</T></MenuItem>
        {REGION_OPTIONS.map((name, index) => (
          <MenuItem key={name} value={name}>{REGIONS[index]}</MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        size="small"
        label={<T>Ciudad</T>}
        value={filterCity}
        onChange={(e) => setFilterCity(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Precio mín</T>}
          value={filterPriceMin}
          onChange={(e) => setFilterPriceMin(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Precio máx</T>}
          value={filterPriceMax}
          onChange={(e) => setFilterPriceMax(e.target.value)}
        />
      </Box>
      <Button
        fullWidth
        size="small"
        onClick={handleClearFilters}
        sx={{ textTransform: "none" }}
      >
        <T>Limpiar filtros</T>
      </Button>
    </>
  );

  const renderResults = () => (
    <>
      {hasActiveFilters && (
        <Typography variant="h4" align="center" sx={{ mb: 4, fontWeight: "bold", color: "text.primary" }}>
          <T>Resultados</T>
        </Typography>
      )}
      <Grid container>
        {loading
          ? [1, 2, 3, 4, 5, 6].map((n) => (
              <Grid item xs={12} key={n}>
                <SkeletonPlaceCard />
              </Grid>
            ))
          : filteredPlaces.length > 0
          ? filteredPlaces.filter(social.isVisible).slice(0, placesLimit).map((place) => (
              <Grid item xs={12} key={place.id}>
                <ListingPost
                  item={place}
                  followType="place"
                  social={social}
                  photo={authorPhotos[place.uid]}
                  featured={isFeatured(place)}
                  onSimilar={(place) => setSelectedType(place.category || "")}
                  similarLabel={<T>Más propiedades similares</T>}
                  media={
                    <Box sx={listingMediaSx}>
                      <ListingImageSlideshow
                        images={place.imageUrls}
                        alt={place.title}
                        height={listingImageHeight}
                        borderRadius={listingImageRadius}
                        PlaceholderIcon={FlightIcon}
                        placeholderText={<T>Sin imágenes</T>}
                      />
                    </Box>
                  }
                >
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700 }} noWrap>
                    {place.title}
                  </Typography>
                  <Typography variant="h6" sx={{ color: brandColors.accent, fontWeight: 800, mb: 1 }}>
                    ${place.price}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                    {place.city && <Chip size="small" icon={<LocationOnIcon />} label={place.city} />}
                    {place.place && <Chip size="small" label={place.place} />}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(`/trips/${place.id}`)}
                  >
                    <T>Ver más</T>
                  </Button>
                </ListingPost>
              </Grid>
            ))
          : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: "45vh",
                  gap: 1.5,
                  color: "text.secondary",
                }}
              >
                <FlightIcon sx={{ fontSize: 56, color: "text.disabled" }} />
                <Typography sx={{ color: "text.secondary" }}>
                  <T>No encontramos publicaciones.</T>
                </Typography>
                <Button variant="contained" onClick={handleClearFilters}>
                  <T>Limpiar búsqueda</T>
                </Button>
              </Box>
            </Grid>
          )}
      </Grid>
      {!loading && (filteredPlaces.length > placesLimit || !placesFeed.exhausted) && (
        <Box ref={placesSentinelRef} sx={{ height: 1 }} />
      )}
      {!loading && filteredPlaces.length > 0 && (
        <Typography
          variant="caption"
          align="center"
          sx={{ display: "block", color: brandColors.textSecondary, mt: 4, mb: 4 }}
        >
          © 2026 Dapur Tecnología - Notiline
        </Typography>
      )}
    </>
  );

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      {showTopBar && (
      <AppBar ref={appBarRef} position="fixed" elevation={0} sx={styles.header}>
        {!sidebarActive && (
        <Toolbar sx={styles.logoToolbar}>
          <CardMedia
            component="img"
            image={themeMode === 'dark' ? logo5 : brandColors.mode === 'light' ? logo1 : logoSvg}
            title="Logo"
            style={styles.logo}
          />
        </Toolbar>
        )}
        <CustomToolbar
          isSearchOpen={isSearchOpen}
          onToggleSearch={toggleSearch}
          onOpenFilters={() => setFiltersOpen(true)}
          showSearchFilters={!isDesktop}
        />
        {!isDesktop && isSearchOpen && (
          <Box sx={{ backgroundColor: sidebarActive ? "transparent" : barColors.background, px: 2, py: 1 }}>
            <Box sx={{ ...pageContentSx, display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                autoFocus
                size="small"
                placeholder="Buscar por título, ciudad o descripción"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{ endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />, startAdornment: <SearchIcon sx={{ mr: 1, color: brandColors.textSecondary }} /> }}
              />
            </Box>
          </Box>
        )}
      </AppBar>
      )}

      <Container maxWidth={false} disableGutters sx={{ width: "100%", mt: `${appBarHeight}px`, mb: 10 }}>
        {!isDesktop && (
          <Drawer
            anchor="bottom"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh", overflowX: "hidden" } }}
          >
            <Box sx={{ pt: 1.5, pb: 4, px: 3, width: "100%", maxWidth: `${PAGE_CONTENT_MAX_WIDTH}px`, mx: "auto", boxSizing: "border-box" }}>
              <SheetDragHandle onClose={() => setFiltersOpen(false)} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}><T>Filtros</T></Typography>
                <IconButton onClick={() => setFiltersOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              {filtersFields}
            </Box>
          </Drawer>
        )}
        <Box
          sx={{
            display: isDesktop ? "grid" : "block",
            gridTemplateColumns: isDesktop ? "260px 1fr" : undefined,
            columnGap: isDesktop ? 3 : 0,
            width: "100%",
            maxWidth: isDesktop ? "1200px" : `${PAGE_CONTENT_MAX_WIDTH}px`,
            mx: "auto",
            pt: isDesktop ? "20px" : "16px",
            px: 2,
            boxSizing: "border-box",
          }}
        >
          {isDesktop && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, position: "sticky", top: appBarHeight + 16, alignSelf: "start", maxHeight: `calc(100vh - ${appBarHeight + 16 + FILTER_PANEL_BOTTOM_GAP}px)`, ...filterPanelScrollSx }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}><T>Filtros</T></Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por título, ciudad o descripción"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                sx={{ mb: 3 }}
                InputProps={{ endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />, startAdornment: <SearchIcon sx={{ mr: 1, color: brandColors.textSecondary }} /> }}
              />
              {filtersFields}
            </Paper>
          )}
          <Box sx={{ minWidth: 0 }}>
            {renderResults()}
          </Box>
        </Box>
      </Container>

      <BottomToolbar />
    </Container>
  );
};

export default TripsPage;
